/**
 * backgroundRemoval - edge-connected background removal for uploaded logos.
 *
 * Replaces the old global colour key (CustomizationEngine.removeBackground),
 * which keyed EVERY pixel close to the corner colour and demonstrably ate
 * logos: a pure-black mark on an already-transparent PNG vanished entirely
 * (corners sample as (0,0,0), so black keyed out), near-white marks on a
 * white background vanished, and white interiors enclosed by a logo mark
 * were punched out.
 *
 * This implementation only removes background-coloured pixels that are
 * CONNECTED TO THE IMAGE BORDER (flood fill from the edges), so interior
 * regions that merely share the background colour survive. If the image
 * corners are already transparent there is nothing to remove and the
 * image is reported as skipped, untouched.
 */

export interface BackgroundRemovalResult {
    /** Number of pixels whose alpha was set to 0. */
    removedCount: number;
    /** True when the image already had a transparent background. */
    skipped: boolean;
}

/** Sum of absolute per-channel differences - same metric the old key used. */
function colorDistance(data: Uint8ClampedArray, idx: number, r: number, g: number, b: number): number {
    return Math.abs(data[idx] - r) + Math.abs(data[idx + 1] - g) + Math.abs(data[idx + 2] - b);
}

/**
 * Remove the background from raw RGBA pixel data, in place.
 *
 * @param data      RGBA bytes (length = width * height * 4); mutated.
 * @param width     Image width in pixels.
 * @param height    Image height in pixels.
 * @param tolerance Sum-of-channel-differences budget for "same colour as
 *                  the background" (default 60). Connectivity to the border
 *                  is what protects the foreground, so this can be more
 *                  forgiving than the old global key's 40 without eating
 *                  the logo itself.
 */
export function removeConnectedBackground(
    data: Uint8ClampedArray,
    width: number,
    height: number,
    tolerance: number = 60
): BackgroundRemovalResult {
    if (width <= 0 || height <= 0 || data.length < width * height * 4) {
        return { removedCount: 0, skipped: true };
    }

    // Sample the background colour from the corner pixels. Only opaque
    // corners count: if most corners are already transparent, the image
    // already has a transparent background and must be left exactly as is
    // (the old key sampled (0,0,0) here and deleted dark logos wholesale).
    const cornerIdx = [
        0,
        (width - 1) * 4,
        ((height - 1) * width) * 4,
        ((height - 1) * width + (width - 1)) * 4
    ];
    let bgR = 0, bgG = 0, bgB = 0, opaqueCorners = 0;
    for (const idx of cornerIdx) {
        if (data[idx + 3] >= 128) {
            bgR += data[idx]; bgG += data[idx + 1]; bgB += data[idx + 2];
            opaqueCorners++;
        }
    }
    if (opaqueCorners < 2) {
        return { removedCount: 0, skipped: true };
    }
    bgR /= opaqueCorners; bgG /= opaqueCorners; bgB /= opaqueCorners;

    const isBackgroundLike = (px: number): boolean => {
        const idx = px * 4;
        if (data[idx + 3] === 0) return true; // already transparent: passable, nothing to remove
        return colorDistance(data, idx, bgR, bgG, bgB) <= tolerance;
    };

    // Flood fill from every border pixel through background-like pixels.
    const total = width * height;
    const visited = new Uint8Array(total);
    const stack: number[] = [];
    const seed = (px: number) => {
        if (!visited[px] && isBackgroundLike(px)) {
            visited[px] = 1;
            stack.push(px);
        }
    };
    for (let x = 0; x < width; x++) {
        seed(x);
        seed((height - 1) * width + x);
    }
    for (let y = 0; y < height; y++) {
        seed(y * width);
        seed(y * width + (width - 1));
    }

    let removedCount = 0;
    while (stack.length > 0) {
        const px = stack.pop()!;
        const idx = px * 4;
        if (data[idx + 3] !== 0) {
            data[idx + 3] = 0;
            removedCount++;
        }
        const x = px % width;
        const y = (px - x) / width;
        if (x > 0) seed(px - 1);
        if (x < width - 1) seed(px + 1);
        if (y > 0) seed(px - width);
        if (y < height - 1) seed(px + width);
    }

    // Soft fringe: surviving pixels that touch a removed pixel and sit
    // within twice the tolerance of the background colour are the ink's
    // anti-aliased edge gradient (plus scan shadow). Giving them a
    // distance-proportional alpha blends the mark onto the check instead
    // of leaving the hard pale halo a binary cut leaves on scans.
    // Interior regions are untouched: they do not touch removed pixels.
    for (let px = 0; px < total; px++) {
        const idx = px * 4;
        if (data[idx + 3] === 0) continue;
        const dist = colorDistance(data, idx, bgR, bgG, bgB);
        if (dist <= tolerance || dist > tolerance * 2) continue;
        const x = px % width;
        const y = (px - x) / width;
        const touchesRemoved =
            (x > 0 && data[idx - 4 + 3] === 0) ||
            (x < width - 1 && data[idx + 4 + 3] === 0) ||
            (y > 0 && data[idx - width * 4 + 3] === 0) ||
            (y < height - 1 && data[idx + width * 4 + 3] === 0);
        if (touchesRemoved) {
            data[idx + 3] = Math.round((255 * (dist - tolerance)) / tolerance);
        }
    }

    return { removedCount, skipped: false };
}

/**
 * Detect where the left-hand logo cluster ends and the write-up block
 * begins, from the alpha channel of a background-removed image.
 *
 * The column profile of opaque pixels is scanned from the first content
 * column: the split is the start of the first wide vertical valley (a
 * run of effectively-empty columns) after a minimum cluster width -
 * exactly the whitespace gap between a logo mark and its text block.
 * Returns a crop width in pixels: everything left of it is the logo
 * cluster. When no qualifying valley exists (single cluster, or text
 * fused to the mark), returns the end of the content itself, so
 * "logo only" degrades to a tight crop of the whole block rather than
 * cutting through content. A column counts as content only above a
 * small pixel threshold, so stray specks left by the filter cannot
 * bridge the valley.
 */
/**
 * Mask of "streak" rows: rows whose opaque pixels span more than half
 * the rough content width while staying sparse (density < 0.25). A scan
 * of a check header often catches a stray line of neighbouring print
 * (e.g. the microprint signature line above the bank block); such a
 * line streaks across the whole image and would otherwise bridge the
 * logo/write-up valley and stretch the block's bounds. Real content
 * rows are dense or narrow, so they are never streaks. The image
 * itself is never modified - streak rows are only excluded from the
 * split/bounds measurements.
 */
export function streakRowMask(data: Uint8ClampedArray, width: number, height: number): Uint8Array {
    const mask = new Uint8Array(height);
    const rowCount = new Int32Array(height);
    const rowFirst = new Int32Array(height).fill(-1);
    const rowLast = new Int32Array(height).fill(-1);
    let roughFirst = -1, roughLast = -1;
    for (let y = 0; y < height; y++) {
        const rowBase = y * width * 4 + 3;
        for (let x = 0; x < width; x++) {
            if (data[rowBase + x * 4] > 0) {
                rowCount[y]++;
                if (rowFirst[y] === -1) rowFirst[y] = x;
                rowLast[y] = x;
            }
        }
        if (rowCount[y] > 0) {
            if (roughFirst === -1 || rowFirst[y] < roughFirst) roughFirst = rowFirst[y];
            if (rowLast[y] > roughLast) roughLast = rowLast[y];
        }
    }
    if (roughFirst === -1) return mask;
    const roughWidth = roughLast - roughFirst + 1;
    for (let y = 0; y < height; y++) {
        if (rowCount[y] === 0) continue;
        const span = rowLast[y] - rowFirst[y] + 1;
        const density = rowCount[y] / span;
        if (span > roughWidth * 0.5 && density < 0.25) mask[y] = 1;
    }
    return mask;
}

export function detectLogoSplit(data: Uint8ClampedArray, width: number, height: number): number {
    // Column profile excluding streak rows, so stray neighbouring
    // print cannot bridge the logo/write-up valley.
    const streak = streakRowMask(data, width, height);
    let keptRows = 0;
    for (let y = 0; y < height; y++) if (!streak[y]) keptRows++;
    const colThreshold = Math.max(2, Math.round(Math.max(keptRows, 1) * 0.01));
    const colCount = new Int32Array(width);
    for (let y = 0; y < height; y++) {
        if (streak[y]) continue;
        const rowBase = y * width * 4 + 3;
        for (let x = 0; x < width; x++) {
            if (data[rowBase + x * 4] > 0) colCount[x]++;
        }
    }
    let first = -1, last = -1;
    for (let x = 0; x < width; x++) {
        if (colCount[x] > colThreshold) {
            if (first === -1) first = x;
            last = x;
        }
    }
    if (first === -1) return width; // no content at all
    const contentWidth = last - first + 1;

    // The split, if any, sits in the left portion of the block: the
    // logo cluster leads, the write-up follows. Valleys outside this
    // region (e.g. trailing whitespace before an edge speck) are not
    // the split.
    const seamRegionEnd = first + Math.round(contentWidth * 0.62);

    // Fast path: a wide, clean whitespace valley after the logo cluster.
    const gapMin = Math.max(6, Math.round(width * 0.015));
    const minCluster = Math.max(8, Math.round(width * 0.03));
    let run = 0;
    for (let x = first + minCluster; x <= Math.min(last, seamRegionEnd + gapMin); x++) {
        if (colCount[x] <= colThreshold) {
            run++;
            if (run >= gapMin && x - run + 1 <= seamRegionEnd) return x - run + 1;
        } else {
            run = 0;
        }
    }

    // Fallback: projection-profile seam. Real scans often leave only a
    // few near-empty columns (plus filter speckle) between the mark and
    // the write-up, so no clean valley exists. The seam is the MINIMUM
    // of the smoothed profile in the region: the true cluster boundary
    // is the emptiest vertical strip in the block (a full-height pass
    // of clean paper), emptier than any word gap inside the write-up,
    // whose columns still carry the other text lines. It must qualify
    // as a genuine lull (<= 25% of the median content column), or the
    // mark and text are treated as fused and there is no split.
    const contentCounts: number[] = [];
    for (let x = first; x <= last; x++) if (colCount[x] > colThreshold) contentCounts.push(colCount[x]);
    contentCounts.sort((a, b) => a - b);
    const median = contentCounts.length ? contentCounts[Math.floor(contentCounts.length / 2)] : 0;
    if (median <= 0) return last + 1;
    const win = Math.max(3, Math.round(width * 0.006));
    const from = first + Math.round(contentWidth * 0.12);
    const to = seamRegionEnd;
    let bestX = -1, bestAvg = Infinity;
    let windowSum = 0;
    for (let x = from; x <= to + win; x++) {
        if (x <= to) windowSum += colCount[x];
        if (x - win >= from) windowSum -= colCount[x - win];
        if (x >= from + win - 1 && x <= to) {
            const avg = windowSum / win;
            if (avg < bestAvg) { bestAvg = avg; bestX = x - Math.floor(win / 2); }
        }
    }
    if (bestX !== -1 && bestAvg <= median * 0.25) return bestX;
    return last + 1;
}

/**
 * Bounding box of opaque content, optionally restricted to x < maxX.
 * Rows/columns must clear a small pixel threshold so isolated specks
 * left by filtering do not stretch the box. Returns null when empty.
 */
export function contentBounds(
    data: Uint8ClampedArray,
    width: number,
    height: number,
    maxX: number = width
): { x: number; y: number; width: number; height: number } | null {
    const limit = Math.min(maxX, width);
    // Extents are measured over non-streak rows only, so a stray line
    // of neighbouring print caught by the scan does not stretch the
    // block's bounding box.
    const streak = streakRowMask(data, width, height);
    let keptRows = 0;
    for (let y = 0; y < height; y++) if (!streak[y]) keptRows++;
    const colThreshold = Math.max(2, Math.round(Math.max(keptRows, 1) * 0.01));
    const colCount = new Int32Array(limit);
    let y0 = -1, y1 = -1;
    for (let y = 0; y < height; y++) {
        if (streak[y]) continue;
        const rowBase = y * width * 4 + 3;
        let rowHas = false;
        for (let x = 0; x < limit; x++) {
            if (data[rowBase + x * 4] > 0) {
                colCount[x]++;
                rowHas = true;
            }
        }
        if (rowHas) { if (y0 === -1) y0 = y; y1 = y; }
    }
    let x0 = -1, x1 = -1;
    for (let x = 0; x < limit; x++) if (colCount[x] > colThreshold) { if (x0 === -1) x0 = x; x1 = x; }
    if (x0 === -1 || y0 === -1) return null;
    return { x: x0, y: y0, width: x1 - x0 + 1, height: y1 - y0 + 1 };
}

/** Fit (w, h) inside (maxW, maxH) preserving aspect ratio; never upscales. */
export function fitWithin(w: number, h: number, maxW: number, maxH: number): { width: number; height: number } {
    if (w <= 0 || h <= 0) return { width: Math.max(1, maxW), height: Math.max(1, maxH) };
    const scale = Math.min(maxW / w, maxH / h, 1);
    return {
        width: Math.max(1, Math.round(w * scale)),
        height: Math.max(1, Math.round(h * scale))
    };
}

/**
 * Apply edge-connected background removal to an image data URL.
 * Returns a PNG data URL with the background made transparent, or the
 * ORIGINAL data URL unchanged when the image already has a transparent
 * background or anything goes wrong (the logo must never be lost).
 */
export async function applyBackgroundRemovalToDataUrl(
    dataUrl: string,
    maxDimension: number = 640
): Promise<string> {
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('image load failed'));
            el.src = dataUrl;
        });
        const { width, height } = fitWithin(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDimension, maxDimension);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return dataUrl;
        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);
        const result = removeConnectedBackground(imageData.data, width, height);
        if (result.skipped || result.removedCount === 0) return dataUrl;
        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL('image/png');
    } catch {
        return dataUrl;
    }
}

export interface LogoProcessOptions {
    /** Remove the (paper) background so the block blends onto the check. */
    removeBackground: boolean;
    /** Keep only the left logo cluster, excluding the write-up text. */
    logoOnly: boolean;
    /**
     * Manual logo-only crop, as a percentage of the image width.
     * Null/undefined = use the auto-detected split (detectLogoSplit).
     */
    cropPercent?: number | null;
}

export interface LogoProcessResult {
    url: string;
    /** Where the auto-split detected the logo/write-up boundary, % of width. */
    detectedSplitPercent: number | null;
}

function loadImageElement(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('image load failed'));
        el.src = dataUrl;
    });
}

/**
 * Full logo pipeline behind the studio's two user options:
 *  - background or not: edge-connected removal of the (possibly
 *    off-white, security-tinted) paper background, sampled from the
 *    image's own corners;
 *  - write-ups or not: "logo only" crops to the left graphic cluster,
 *    split auto-detected from the filtered alpha's column valley, with
 *    an optional manual crop percentage override.
 * "Keep background" + "include write-ups" returns the upload untouched
 * (the original rectangle). Any failure also returns the upload
 * untouched - the logo must never be lost to processing.
 */
export async function processLogoImage(
    dataUrl: string,
    options: LogoProcessOptions,
    maxDimension: number = 640
): Promise<LogoProcessResult> {
    const untouched: LogoProcessResult = { url: dataUrl, detectedSplitPercent: null };
    if (!options.removeBackground && !options.logoOnly) return untouched;
    try {
        const img = await loadImageElement(dataUrl);
        const { width, height } = fitWithin(img.naturalWidth || img.width, img.naturalHeight || img.height, maxDimension, maxDimension);

        const srcCanvas = document.createElement('canvas');
        srcCanvas.width = width;
        srcCanvas.height = height;
        const srcCtx = srcCanvas.getContext('2d');
        if (!srcCtx) return untouched;
        srcCtx.drawImage(img, 0, 0, width, height);
        const working = srcCtx.getImageData(0, 0, width, height);

        // Probe pass on a copy: gives the alpha the split detection and
        // the content bounds need, even when the user keeps the original
        // (unfiltered) pixels for the final image.
        const probeData = new Uint8ClampedArray(working.data);
        removeConnectedBackground(probeData, width, height);

        let detectedSplitPercent: number | null = null;
        let splitPx = width;
        if (options.logoOnly) {
            splitPx = detectLogoSplit(probeData, width, height);
            detectedSplitPercent = Math.round((splitPx / width) * 1000) / 10;
        }

        if (options.removeBackground) {
            removeConnectedBackground(working.data, width, height);
            srcCtx.putImageData(working, 0, 0);
        }
        const alphaSource = options.removeBackground ? working.data : probeData;

        const cropW = options.logoOnly
            ? Math.max(1, Math.min(width, options.cropPercent != null ? Math.round((width * options.cropPercent) / 100) : splitPx))
            : width;
        const bounds = contentBounds(alphaSource, width, height, cropW);
        if (!bounds) return untouched;

        // Small breathing room around the content, clamped to the image.
        const pad = 3;
        const bx = Math.max(0, bounds.x - pad);
        const by = Math.max(0, bounds.y - pad);
        const bw = Math.min(width - bx, bounds.width + pad * 2);
        const bh = Math.min(height - by, bounds.height + pad * 2);

        const outCanvas = document.createElement('canvas');
        outCanvas.width = bw;
        outCanvas.height = bh;
        const outCtx = outCanvas.getContext('2d');
        if (!outCtx) return untouched;
        outCtx.drawImage(srcCanvas, bx, by, bw, bh, 0, 0, bw, bh);
        return { url: outCanvas.toDataURL('image/png'), detectedSplitPercent };
    } catch {
        return untouched;
    }
}
